import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import ErrorUtils from 'src/utils/ErrorUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { SegmentPeriodRequest } from 'src/services/segment-cdt/SegmentDataTablesService';
import { ColumnPullStatus, PullStatusResponse } from 'src/models/segment-cdt/SegmentCdtTypes';
import {
    columnPullRows,
    isColumnFailed,
    isColumnFinished,
    isTerminal,
    joinReasons,
    runFailureText,
} from 'src/components/segment-cdt/detail/segmentPullStatus';
import { PullInProgressError } from 'src/models/segment-cdt/PullInProgressError';
import SegmentValidationModal from 'src/components/segment-cdt/detail/SegmentValidationModal';
import SegmentUploadModal from 'src/components/segment-cdt/detail/SegmentUploadModal';
import SegmentAddColumnModal from 'src/components/segment-cdt/detail/SegmentAddColumnModal';
import SegmentDownloadModal from 'src/components/segment-cdt/detail/SegmentDownloadModal';

const PULL_POLL_MS = 3000;
// Cap the poll so a status that never reaches a terminal state (or a backend that keeps omitting
// pullStatus for an unknown/expired id) can't spin the button forever. 3s * 200 ≈ 10 minutes, well past
// a normal pull. Mirrors the shared promotion poller's MAX_PROMOTION_POLL_ATTEMPTS safeguard.
const MAX_PULL_POLL_ATTEMPTS = 200;
// Extra attempts to read a recovered pull's detail after a failed read, PER pull. One: the losable thing is a
// popover's contents, not the pull's outcome (which the period reports durably), so this is worth a retry and
// not worth a loop.
const MAX_RECOVERY_ATTEMPTS = 1;

/**
 * What to say when a pull ends, and how loudly — one branch per outcome, because they are four different
 * things and three of them used to read as either "complete" or "failed".
 *
 * The wording carries the server's own failure text where there is one. There is deliberately no "check the
 * column pull statuses": telling someone to go and look is worse than showing them what is already known,
 * and on a run-level failure (a workflow that timed out, or never started) there was nothing for them to
 * find there — the reason sat under a key nothing rendered.
 *
 * `unattributedReason` is for a reason no banner has said: a column that carries a message but was never
 * announced on its own, because nothing said it finished or because it finished before this poll started
 * watching. A column that *did* get its own banner is deliberately absent from here — repeating it would
 * leave two banners saying the same thing, and the per-column one says it better because it names the column.
 */
function terminalAnnouncement(status: PullStatusResponse, unattributedReason?: string): {
    banner: 'success' | 'warning' | 'error' | 'info';
    message: string;
} {
    const rowsUpserted = status.rowsUpserted ?? 0;
    const failureText = runFailureText(status) ?? unattributedReason;
    if (status.overallStatus === ColumnPullStatus.NO_CONFIG) {
        // Nothing was pulled and nothing went wrong. Reporting this as "complete" claimed a pull happened.
        return { banner: 'info', message: 'This table has no GL pull columns, so there was nothing to pull.' };
    }
    if (status.overallStatus === ColumnPullStatus.PARTIAL_FAILURE) {
        // Some columns pulled and some did not, so neither a success nor an outright failure is true.
        return {
            banner: 'warning',
            message: `GL pull finished with failures. ${rowsUpserted} row(s) updated.`
                + (failureText ? ` ${failureText}` : ''),
        };
    }
    if (status.overallStatus === ColumnPullStatus.SUCCESS) {
        return { banner: 'success', message: `GL pull complete. ${rowsUpserted} row(s) updated.` };
    }
    if (failureText) {
        return { banner: 'error', message: `GL pull failed. ${failureText}` };
    }
    // "No reason" is only asserted when the record genuinely carries none. `failureText` being empty is a
    // weaker fact than that: it is also empty when every reason has already been attributed to its column and
    // spoken by a per-column banner, and denying a reason exists next to a banner that just gave one is worse
    // than saying less. So the claim is made from the record, not from what is left to say.
    const recordedNothing = !status.failureReason
        && Object.values(status.errors ?? {}).every((reason) => !reason);
    return {
        banner: 'error',
        message: recordedNothing
            ? 'GL pull failed. The server recorded no reason.'
            : 'GL pull failed.',
    };
}

/**
 * The detail viewer's action toolbar: Pull values (trigger + poll GL pull), Upload (file modal),
 * Download (presigned URL → open), Validate (on-demand report modal). "Add columns" stays a stub for a
 * later CR. Reads `{ state, dispatch, services }` from context; period comes from the detail slice.
 */
export default function SegmentDetailToolbar() {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const svc = services.segmentDataTablesService;
    // tableId is the decoded route param, published to the detail slice by SegmentTableDetailContent —
    // read from context (reliable + immediate) rather than threaded through props.
    const tableId = state.detail.tableId ?? '';
    const period = state.detail.selectedPeriod;
    // Latest selected period, readable from inside an async handler (whose closure otherwise captures the
    // stale click-time period) so a pull triggered for one period can be dropped if the user switches away.
    // Written during render (not in an effect) so it can't lag a period-switch commit — the poll's
    // period-match guard reads it and a deferred effect would leave a sub-frame window of misattribution.
    const periodRef = useRef(period);
    periodRef.current = period;
    // The column set, readable from inside the poll (whose closure would otherwise hold the set as it was
    // when the poll started). Needed only to turn a column id into the name a user recognises.
    const columnsRef = useRef(state.detail.definition?.columns);
    columnsRef.current = state.detail.definition?.columns;

    const [showValidate, setShowValidate] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showAddColumns, setShowAddColumns] = useState(false);
    const [showDownload, setShowDownload] = useState(false);
    // Setting these (to a fresh object) fires the corresponding render-hook; identity change = re-run.
    const [validateReq, setValidateReq] = useState<SegmentPeriodRequest | undefined>(undefined);
    const [downloadReq, setDownloadReq] = useState<SegmentPeriodRequest | undefined>(undefined);
    // The id of the GL pull currently being polled; null = idle. Mirrors usePromotion's
    // inProgressPromotionId: set from the mount/period lookup or a fresh trigger, cleared on a terminal
    // status. `triggering` covers the brief trigger call before an id exists.
    const [pullId, setPullId] = useState<string | null>(null);
    const [triggering, setTriggering] = useState(false);
    // The pull this screen started itself, if any. Read by the poll to decide whether a column finishing is
    // news (we were watching from the start) or history (we attached to a run already under way). A ref, not
    // state: the poll effect reads it and must not re-run when it changes.
    const selfTriggeredPullId = useRef<string | null>(null);
    // True until the mount/period-change lookup resolves. Like usePromotion's `initializing`, it keeps
    // Pull disabled during the initial lookup so a click can't race a late "is a pull already running?"
    // answer.
    const [initializing, setInitializing] = useState(false);
    // True from the Validate click until the fresh report for the requested period arrives, so a
    // re-open never flashes the previous period's report before the spinner.
    const [awaitingValidate, setAwaitingValidate] = useState(false);
    // Set synchronously on the Download click so the button disables in the same render, before the
    // fetch hook's post-render effect flips `downloading`. Without it a fast double-click fires two
    // fetches and opens two tabs. Mirrors awaitingValidate.
    const [awaitingDownload, setAwaitingDownload] = useState(false);

    const [validation, validating, validateError] = svc.getSegmentValidations(validateReq);
    const [download, downloading, downloadError] = svc.downloadSegmentDataTable(downloadReq);

    // Set once the component unmounts so an awaited handler (onPull) doesn't setState on a dead component.
    const unmounted = useRef(false);
    useEffect(() => () => { unmounted.current = true; }, []);

    // Drop the awaiting-flash gate once the validate fetch settles: when a report/error arrives, OR when a
    // fetch we saw go in-flight has finished — the latter clears the spinner even for a resolved-but-empty
    // report (which is otherwise falsy with no error), instead of hanging on it.
    const validateInFlight = useRef(false);
    useEffect(() => {
        if (validating) {
            validateInFlight.current = true;
            return;
        }
        if (validation || validateError || validateInFlight.current) {
            validateInFlight.current = false;
            setAwaitingValidate(false);
        }
    }, [validation, validateError, validating]);

    // Download: when a presigned URL arrives, trigger the download and clear the request so the same
    // period can be downloaded again later. Use a temporary anchor rather than window.open — the URL
    // resolves after an async fetch, outside the click's user-activation window, so window.open would be
    // popup-blocked; an <a> click is not.
    // Drop the download gate once the fetch settles: when a URL/error arrives, OR when a fetch we saw go
    // in-flight has finished (clears the gate even for a resolved-but-empty response instead of hanging).
    // Mirrors the validate in-flight effect.
    const downloadInFlight = useRef(false);
    useEffect(() => {
        if (downloading) {
            downloadInFlight.current = true;
            return;
        }
        if (download?.downloadUrl || downloadError || downloadInFlight.current) {
            downloadInFlight.current = false;
            setAwaitingDownload(false);
        }
    }, [download, downloadError, downloading]);

    useEffect(() => {
        if (download?.downloadUrl) {
            const a = document.createElement('a');
            a.href = download.downloadUrl;
            // Open in a new tab: a presigned S3 URL is cross-origin (so the `download` attribute is ignored)
            // and may lack Content-Disposition, in which case a same-tab navigation would unload the app —
            // target="_blank" keeps the detail view intact whether the URL downloads or renders.
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setDownloadReq(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [download]);
    useEffect(() => {
        if (downloadError) {
            services.messageService.showErrorBanner(downloadError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadError]);

    const needPeriod = (): boolean => {
        if (!period) {
            services.messageService.showErrorBanner('Select a period first.');
            return false;
        }
        return true;
    };

    const onValidate = () => {
        if (!needPeriod()) return;
        setAwaitingValidate(true); // spinner until this period's fresh report arrives
        setValidateReq({ tableId, period: period as string }); // fresh object → re-fetch
        setShowValidate(true);
    };

    // Confirm first: the workbook is a read-only review artefact and the request is otherwise silent, so the
    // dialog is where the period and the sheets it will contain are stated. The period is checked before opening
    // rather than on confirm, so the dialog never appears naming a period that is not selected.
    const onDownload = () => {
        if (!needPeriod()) return;
        setShowDownload(true);
    };

    const startDownload = () => {
        setShowDownload(false);
        setAwaitingDownload(true); // disable synchronously so a fast double-click can't open two tabs
        setDownloadReq({ tableId, period: period as string });
    };

    // ── Mount / period-change lookup ──────────────────────────────────────────────────────────────
    // Discover a pull already in flight for this table+period (the backend reports it by tableId+period
    // for a caller — like a reload — that no longer holds the id). Like PromoteButton's initial
    // GetPromotionStatus: silent, no banner, it only decides whether the button already shows "Pulling…".
    // Resetting pullId to null up front stops a prior period's poll on a period switch; the lookup then
    // ONLY sets an id when a pull is genuinely in flight, so a late "idle" answer can't clobber a pull the
    // user just triggered (the button is disabled while `initializing`, so no click races the lookup).
    useEffect(() => {
        setPullId(null);
        if (!period || !tableId) {
            setInitializing(false);
            return;
        }
        let cancelled = false;
        setInitializing(true);
        svc.getCDTGLPullStatusByTable(tableId, period as string)
            .then((status) => {
                if (cancelled) return;
                if (status && !isTerminal(status) && status.pullRequestId) {
                    setPullId(status.pullRequestId);
                    // Published here as well as in the poll so a reload mid-pull reports progress at once
                    // rather than showing nothing until the first tick lands one interval later.
                    dispatch(ACTIONS.SET_DETAIL_PULL_STATUS.withPayload(status));
                }
                setInitializing(false);
            })
            .catch(() => { if (!cancelled) setInitializing(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableId, period]);

    // ── Recover the last pull's detail after a reload ─────────────────────────────────────────────
    // The period's own record names the pull that last ran against it, so its per-column statuses, reasons
    // and rates are recoverable rather than lost with the id. Without this a reloaded page could say only
    // that the last pull failed, with nothing to show for it.
    //
    // Read-only and one-shot: it publishes a status but never sets `pullId`, so it cannot start a poll or
    // disable the button. It is skipped whenever a pull is being tracked or a status is already published,
    // so it can never overwrite a live poll's reading with an older pull's.
    //
    // `initializing` is deliberately NOT a condition or a dependency. It flips twice while the mount lookup
    // resolves, and gating on it re-ran this effect mid-flight: the cleanup dropped the in-flight read and
    // the id-already-read guard then refused to try again, so the recovery silently never happened. What
    // that gate was protecting against is covered instead by `pullId` — if the lookup does find a live pull,
    // that dependency changes and this read's result is dropped, which is the right outcome.
    // Keyed on period AND id, and recording SUCCEEDED rather than ATTEMPTED.
    //
    // Latching before the read and never clearing made this "we have tried once", which permanently refused a
    // retry the guard was never meant to block: a transient throttle or 5xx lost that pull's breakdown for the
    // life of the component, and a period switch mid-flight had its read dropped by the cleanup and then
    // refused on return. Including the period also means returning to a period re-reads its own pull rather
    // than being blocked by a sibling's id.
    const recoveredFor = useRef<string | null>(null);
    // The retry allowance, scoped to the SAME key as the guard above rather than to the component.
    //
    // A component-scoped counter starved every later period: once one pull had spent the allowance, the next
    // period's own transient failure got no retry, which contradicts the point of keying the guard per period.
    // Holding the key alongside the count means each pull gets its own allowance and a persistently failing
    // read still cannot spin, because the count only resets when the key actually changes.
    const retryBudget = useRef<{ key: string; spent: number }>({ key: '', spent: 0 });
    // Purely the re-render trigger. Clearing the guard is not enough on its own: with unchanged dependencies
    // React never re-runs the effect, so a transient failure would sit unrecovered until something else
    // changed. Monotonic, so it can never itself refuse a retry — the budget above is what bounds them.
    const [recoveryNudge, setRecoveryNudge] = useState(0);
    const lastPullRequestId = (state.detail.periods ?? [])
        .find((p) => p.period === period)?.lastPullRequestId;
    useEffect(() => {
        if (!lastPullRequestId || pullId) return;
        if (state.detail.pullStatus) return;
        const forPeriod = periodRef.current;
        const attempt = `${forPeriod ?? ''}:${lastPullRequestId}`;
        if (recoveredFor.current === attempt) return;
        let cancelled = false;
        svc.getCDTGLPullStatus(lastPullRequestId)
            .then((status) => {
                // Dropped if the user has moved on: this pull belongs to the period it was read for.
                if (cancelled || unmounted.current || !status || periodRef.current !== forPeriod) return;
                // Marked only once a usable answer is in hand and still relevant, so every path that did not
                // produce one stays retryable.
                recoveredFor.current = attempt;
                if (isTerminal(status)) {
                    dispatch(ACTIONS.SET_DETAIL_PULL_STATUS.withPayload(status));
                    return;
                }
                // The named pull is still running. Publishing it and stopping there would freeze an
                // in-progress indicator that nothing advances, with the Pull button still offering to start
                // another — so it is handed to the poller instead, which publishes every tick and announces
                // the outcome. Reachable rather than theoretical: the mount lookup asks only about UI-source
                // pulls, so a month-end-close pull in flight is invisible to it and is named only here.
                setPullId(lastPullRequestId);
            })
            // No banner, deliberately: the period's own durable last-pull fields still state the outcome, so
            // the row loses only the per-column breakdown — nothing to interrupt the user about, on a read
            // they did not ask for. Logged rather than swallowed outright, because a silently missing popover
            // is otherwise indistinguishable from a pull that had no per-column detail. The guard above is
            // left unset, so the next render retries.
            .catch((ex) => {
                console.warn('Could not recover GL pull detail for', lastPullRequestId, ex);
                // No banner: the period's own durable last-pull fields still state the outcome, so the row
                // loses only the per-column breakdown — nothing to interrupt the user about on a read they did
                // not ask for. One retry, because the common causes (throttle, 5xx) clear on their own.
                if (cancelled || unmounted.current) {
                    return;
                }
                if (retryBudget.current.key !== attempt) {
                    retryBudget.current = { key: attempt, spent: 0 };
                }
                if (retryBudget.current.spent < MAX_RECOVERY_ATTEMPTS) {
                    retryBudget.current.spent += 1;
                    setRecoveryNudge((nudge) => nudge + 1);
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastPullRequestId, pullId, recoveryNudge]);

    /**
     * Announces the columns that finished since the last look, so a long pull reports progress per column
     * rather than only at the end.
     *
     * One banner for the columns that succeeded (they are one piece of news, and a table with ten GL columns
     * would otherwise produce ten banners), and one per failed column, carrying the reason the server gave —
     * a failure is individually actionable and its message is the actionable part.
     *
     * `seeded` false means this is the first look at this pull: what is already finished is recorded as seen
     * without being announced, because it finished before this poll started watching.
     *
     * **`seen` and `spoken` are tracked separately, and the difference is load-bearing.** `seen` stops a
     * column being announced twice; `spoken` records what a banner actually said, and is what the terminal
     * summary subtracts. Conflating them dropped a reason entirely on a pull whose first poll was already
     * terminal: the column was recorded as seen, suppressed for being unseeded, and then excluded from the
     * summary for having been "announced" when nothing had been.
     */
    const announceFinishedColumns = (status: PullStatusResponse | undefined, seen: Set<string>,
                                     spoken: Set<string>, seeded: boolean) => {
        const finished = columnPullRows(status, columnsRef.current)
            .filter((row) => isColumnFinished(row) && !seen.has(row.columnId));
        finished.forEach((row) => seen.add(row.columnId));
        if (!seeded || !finished.length) {
            return;
        }
        const succeeded = finished.filter((row) => !isColumnFailed(row));
        if (succeeded.length) {
            services.messageService.showInfoAutoDismissBanner(
                `Pulled ${succeeded.map((row) => row.label).join(', ')}.`);
        }
        finished.filter((row) => isColumnFailed(row)).forEach((row) => {
            services.messageService.showErrorBanner(
                `${row.label} failed to pull.${row.error ? ` ${row.error}` : ''}`);
        });
        finished.forEach((row) => spoken.add(row.columnId));
    };

    /**
     * The reasons no banner has said: a column carrying a message that was never announced on its own —
     * because nothing said it finished, or because it finished before this poll started watching. Without
     * this their reasons reach nobody; with it, and only it, the summary adds nothing a per-column banner has
     * already stated.
     */
    const unattributedReason = (status: PullStatusResponse | undefined, spoken: Set<string>) =>
        joinReasons(columnPullRows(status, columnsRef.current)
            .filter((row) => !spoken.has(row.columnId))
            // A column that succeeded is excluded even when it carries a message, because a reason can be
            // recorded before a retry that then worked — the invariant `isColumnFailed` documents. Without
            // this, a SUCCESS column's superseded "GL timeout on attempt 1" is reported as the run's cause and
            // points a finance reader at the wrong column. A column with a reason and no status yet is kept:
            // it is not a success, and its reason is exactly what no banner has said.
            .filter((row) => !isColumnFinished(row) || isColumnFailed(row))
            .map((row) => row.error));

    // ── Poll while a pull is in flight (pullId set) ───────────────────────────────────────────────
    // Capped so a stuck/unknown pull can't spin the button forever (mirrors MAX_PROMOTION_POLL_ATTEMPTS).
    // Cancels on unmount and whenever pullId changes (e.g. a period switch), so a completed pull for one
    // period can't refresh another.
    useEffect(() => {
        if (!pullId) return;
        // The period this pull belongs to. The [pullId] effect's cleanup lags one commit on a period
        // switch, so guard the terminal branch: if the user has moved to another period by the time the
        // pull finishes, stop tracking it but don't banner/refresh the period they're now viewing.
        const pollPeriod = periodRef.current;
        let cancelled = false;
        let attempts = 0;
        let timer: ReturnType<typeof setTimeout> | null = null;
        // Columns this poll has seen finish, and the subset a banner has actually named. The first is
        // seeded rather than announced on the first look: a page that reloads mid-pull attaches to a run
        // whose earlier columns finished before anyone here was watching, and announcing those as fresh
        // news would be a lie about when they happened. The second is what the summary subtracts, so a
        // reason that was suppressed is still reported somewhere.
        const seenColumns = new Set<string>();
        const spokenColumns = new Set<string>();
        // For a pull THIS screen started, seeded from the outset: the user was watching before it began, so
        // every column that finishes is news. Deliberately not "after the first successful poll" - that
        // conflated "we were not watching" with "no request has come back yet", so a single throttle or 5xx on
        // the very first tick silently swallowed the whole first wave of banners on a pull the user had just
        // triggered and was staring at. For an attached pull it stays false until the first observation, which
        // is correct: those columns did finish before anyone here was looking.
        let seeded = selfTriggeredPullId.current === pullId;
        const poll = async () => {
            attempts += 1;
            try {
                const status = await svc.getCDTGLPullStatus(pullId);
                if (cancelled) return;
                // Publish every tick, terminal included, so the metadata row's per-column progress comes
                // from THIS poll rather than a second one. Guarded on the period for the same reason the
                // terminal branch below is: a pull finishing after the user moved away must not label the
                // period they are now looking at.
                if (periodRef.current === pollPeriod) {
                    dispatch(ACTIONS.SET_DETAIL_PULL_STATUS.withPayload(status));
                }
                if (isTerminal(status)) {
                    setPullId(null);
                    if (periodRef.current !== pollPeriod) return; // user switched periods — not their pull to announce
                    // Announce the columns that finished on this last tick before summarising, so a column
                    // that only failed at the end is still named — and so the summary below can leave out
                    // every reason that has now been said against its own column.
                    announceFinishedColumns(status, seenColumns, spokenColumns, seeded);
                    seeded = true;
                    const { banner, message } = terminalAnnouncement(
                        status as PullStatusResponse,
                        unattributedReason(status, spokenColumns));
                    if (banner === 'success') {
                        services.messageService.showSuccessAutoDismissBanner(message);
                    } else if (banner === 'warning') {
                        services.messageService.showWarningBanner(message);
                    } else if (banner === 'info') {
                        services.messageService.showInfoAutoDismissBanner(message);
                    } else {
                        services.messageService.showErrorBanner(message);
                    }
                    // Refresh on EVERY terminal outcome, unconditionally.
                    //
                    // This action is the only thing that bumps `detail.refreshToken`, and that token is the
                    // only input to the period refetch — so it does not just reload rows, it is what replaces
                    // the period's durable last-pull record. Gating it meant a pull that failed writing no
                    // rows left the PREVIOUS pull's verdict on screen: switch period and back, and the row
                    // reported "Completed" with the old timestamp, which is the exact misstatement the durable
                    // verdict exists to remove.
                    //
                    // Two further reasons not to gate on rows written. `rowsUpserted` is nullable, so an
                    // absent count is not evidence that nothing changed. And NO_CONFIG hardcodes zero while
                    // still reconciling segment rows — seeding and deleting them — so a table whose value set
                    // changed did write, and reported nothing.
                    //
                    // The saving this replaces was one refetch of rows that "could not have changed"; the cost
                    // was showing a verdict that was not true.
                    dispatch(ACTIONS.REFRESH_DETAIL_ROWS);
                    return;
                }
                // Still running: report each column as it finishes, so a long pull shows progress instead of
                // a spinner.
                if (periodRef.current === pollPeriod) {
                    announceFinishedColumns(status, seenColumns, spokenColumns, seeded);
                    seeded = true;
                }
            } catch (ex) {
                if (cancelled) return;
                const status = (ex as { status?: number } | undefined)?.status;
                // A definitive client error — an unknown/expired pullRequestId (417) or a 403 — never
                // resolves, so stop polling and surface the server's message rather than spinning to the
                // ~10-min cap and then mis-describing it as "taking longer than expected". Throttling and
                // timeout (429/408) are transient and must keep retrying.
                const isDefinitive = typeof status === 'number'
                    && status >= 400 && status < 500 && status !== 408 && status !== 429;
                if (isDefinitive) {
                    setPullId(null);
                    // Don't announce for a period the user has already left (the [pullId] cleanup lags one
                    // commit on a period switch) — mirror the terminal-success branch's guard.
                    if (periodRef.current !== pollPeriod) return;
                    // Drop the last known status: polling has stopped, so leaving the final in-progress
                    // reading on screen would show a pull as still running that nothing is tracking.
                    dispatch(ACTIONS.SET_DETAIL_PULL_STATUS.withPayload(undefined));
                    services.messageService.showErrorBanner(
                        ErrorUtils.getMessage(ex) || 'GL pull status is unavailable.');
                    return;
                }
                // Transient (5xx / network / throttle): keep polling, bounded by the attempt cap. WARN so an
                // erroring status endpoint is visible to oncall rather than silently absorbed.
                // eslint-disable-next-line no-console
                console.warn('GL pull status poll failed; will retry', ex);
            }
            if (cancelled) return;
            if (attempts >= MAX_PULL_POLL_ATTEMPTS) {
                setPullId(null);
                // Kept, not cleared, unlike the definitive-error branch: the last reading is still the truth
                // as far as we know it, and it is what the metadata row goes on showing.
                //
                // Worded as what actually happened. Nothing has failed and nothing was cancelled — the pull
                // is still running on the server, this page has simply stopped watching it — so the banner
                // says that and says what to do, rather than telling the user to go and check statuses that
                // will not change while nothing is polling them.
                services.messageService.showWarningBanner(
                    'GL pull is still running and this page has stopped tracking it. '
                    + 'Reload to pick it up again.');
                return;
            }
            timer = setTimeout(poll, PULL_POLL_MS);
        };
        poll();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pullId]);

    const onPull = async () => {
        if (!needPeriod()) return;
        const requestedPeriod = period;
        setTriggering(true);
        try {
            const id = await svc.triggerCDTGLPull(tableId, requestedPeriod as string);
            // Drop the result if the toolbar unmounted or the user switched periods mid-trigger — otherwise
            // we'd poll the old period's pull and refresh the now-current, different period on completion.
            if (unmounted.current || periodRef.current !== requestedPeriod) return;
            selfTriggeredPullId.current = id || null;
            setPullId(id || null); // hands off to the polling effect; `|| null` so an empty id can't stick the button
        } catch (ex) {
            // Drop the result if the toolbar unmounted or the user switched periods mid-trigger — otherwise a
            // 409-resume or an error would be attributed to (and poll) the period they are now viewing.
            if (unmounted.current || periodRef.current !== requestedPeriod) return;
            if (ex instanceof PullInProgressError) {
                // A pull is already running (started in another tab, or missed by the mount lookup on a
                // timing edge). Attach to it and resume polling rather than showing a generic failure.
                setPullId(ex.pullRequestId);
            } else {
                services.messageService.showErrorBanner(ErrorUtils.getMessage(ex) || 'Could not start the GL pull.');
            }
        } finally {
            if (!unmounted.current) setTriggering(false);
        }
    };

    return (
        <>
            <SpaceBetween direction="horizontal" size="xs">
                <Button loading={triggering || !!pullId} disabled={initializing || triggering || !!pullId} onClick={onPull}>Pull values</Button>
                <Button iconName="upload" onClick={() => setShowUpload(true)}>Upload</Button>
                {/* Also disabled until the definition resolves: the confirmation dialog names the table, and
                    opening it beforehand would state the period against a blank name. */}
                <Button
                    iconName="download"
                    loading={downloading || awaitingDownload}
                    disabled={downloading || awaitingDownload || !state.detail.definition}
                    onClick={onDownload}
                >
                    Download
                </Button>
                <Button loading={validating || awaitingValidate} disabled={validating || awaitingValidate} onClick={onValidate}>Validate</Button>
                {/* Disabled until the definition GET resolves: the add session seeds the segment (for the
                    CoA lookups) from the definition, so opening before it loads would leave the segment unset.

                    Known limitation (deferred): the grid renders the table-wide `definition.columns` for every
                    period, so after an add an earlier/closed period shows the new column as a blank extra.
                    It can only ever over-render a blank column, never hide one a period actually had —
                    columns are add-only and immutable, so the current set is always a superset of any earlier
                    period's, and closed-period LEGEND rows are untouched. The same applies while *viewing* a
                    closed period: the target period is resolved server-side to the open one regardless of
                    what is on screen, so the blank column appears immediately and the subsequent
                    REFRESH_DETAIL_ROWS re-fetches that closed period (which will not carry it). One cause,
                    one fix: a per-period GetSegmentDataTable. */}
                <Button iconName="add-plus" disabled={!state.detail.definition} onClick={() => setShowAddColumns(true)}>Add columns</Button>
            </SpaceBetween>
            <SegmentValidationModal
                visible={showValidate}
                report={awaitingValidate ? undefined : validation?.report}
                loading={validating || awaitingValidate}
                error={awaitingValidate ? undefined : validateError}
                onDismiss={() => setShowValidate(false)}
            />
            {/* Reads its own state/dispatch from context and handles the post-upload refresh/period-switch
                itself — no prop chain. */}
            <SegmentUploadModal visible={showUpload} onDismiss={() => setShowUpload(false)} />
            {/* Reuses the create-wizard column editor (chooser + GL/Manual/Derived forms + queued-columns
                slice) and commits via AddSegmentColumns, then refreshes the grid from the response. */}
            <SegmentAddColumnModal visible={showAddColumns} onDismiss={() => setShowAddColumns(false)} />
            {/* States what the review workbook covers before the request is made; reads the table name and
                period from context itself. Confirming hands back to startDownload, which owns the fetch. */}
            <SegmentDownloadModal
                visible={showDownload}
                downloading={downloading || awaitingDownload}
                onDismiss={() => setShowDownload(false)}
                onConfirm={startDownload}
            />
        </>
    );
}
